// @flow

import * as React from "react"
import {connect} from "react-redux"
import ClassNames from "classnames"
import {withTranslation} from "react-i18next"
import {format, addMonths} from "date-fns"
import Icon from "../../components/icon"
import Flatpickr from "react-flatpickr"
import {
  setSearchItems,
  markAllAsPhotographed,
  closeAllSearchItems,
} from "../../actions/search-items"
import {
  getSearchListItemsByCategory,
  getUrgentSearchListItems,
  getCriticalSearchListItems,
  getItemForStandalone,
} from "../../reducers/search-items"
import Config from "../../config"
import SearchItemList from "../../components/search-item-list"
import Select from "react-select"
import type {
  State as ReduxState,
  SearchListItem,
  SearchItemCategory,
  Supplier,
  Dispatch,
} from "../../types"

type Props = {
  dispatch: Dispatch,
  searchListItems: $ReadOnlyArray<SearchListItem>,
  categories: $ReadOnlyArray<SearchItemCategory>,
  suppliers: $ReadOnlyArray<Supplier>,
  toggledSearchListItems: $ReadOnlyArray<number>,
  foundRemarksToggleds: $ReadOnlyArray<number>,
  +isStandalone: boolean,
  t: any,
}

type ListType = "all" | "urgent" | "critical"

type State = {
  listType: ListType,
  babyId: ?number,
  categoryId: number,
  toggledFoundRemarks: boolean,
  filterCirculars: boolean,
  filterStars: boolean,
  urgentEndDate: Date,
  suppliersFilter: $ReadonlyArray<number>,
}

class SearchItemsDashboardView extends React.Component<Props, State> {
  handleListTypeClick: (SyntheticMouseEvent<HTMLLinkElement>, ListType) => void
  handleCategoryChange: (SyntheticInputEvent<HTMLSelectElement>) => void
  handleTogglePhotographed: (SyntheticMouseEvent<HTMLLinkElement>) => void
  handleToggleCirculars: (SyntheticMouseEvent<HTMLLinkElement>) => void
  handleToggleStars: (SyntheticMouseEvent<HTMLLinkElement>) => void
  handleCloseAllItems: (SyntheticMouseEvent<HTMLLinkElement>) => void
  handleUrgentDateChange: ($ReadOnlyArray<Date>) => void

  el: ?HTMLElement

  constructor(props: Props) {
    super(props)
    this.state = {
      listType: "all",
      babyId: Config.data.babyId,
      categoryId: -1,
      suppliersFilter: [],
      toggledFoundRemarks: false,
      filterCirculars: false,
      filterStars: false,
      urgentEndDate: addMonths(new Date(), 2),
    }
    this.handleListTypeClick = this.handleListTypeClick.bind(this)
    this.handleCategoryChange = this.handleCategoryChange.bind(this)
    this.handleUrgentDateChange = this.handleUrgentDateChange.bind(this)
    this.handleTogglePhotographed = this.handleTogglePhotographed.bind(this)
    this.handleToggleCirculars = this.handleToggleCirculars.bind(this)
    this.handleToggleStars = this.handleToggleStars.bind(this)
    this.handleCloseAllItems = this.handleCloseAllItems.bind(this)
    this.handleSuppliersChange = this.handleSuppliersChange.bind(this)
  }

  componentDidMount() {
    // for dashboard views, the base entities are already output in the html
    const {dispatch} = this.props
    dispatch(
      setSearchItems(Config.data.searchItems, Config.data.searchItemSuppliers)
    )
  }

  handleListTypeClick(
    e: SyntheticMouseEvent<HTMLLinkElement>,
    listType: ListType
  ) {
    e.preventDefault()
    this.setState({listType})
  }

  handleCategoryChange(e: SyntheticInputEvent<HTMLSelectElement>) {
    const categoryId = parseInt(e.currentTarget.value)
    this.setState({categoryId}, () => this.scrollVisible(categoryId))
  }

  handleTogglePhotographed(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    const {dispatch, searchListItems} = this.props
    dispatch(markAllAsPhotographed(searchListItems))
  }

  handleCloseAllItems(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    const {dispatch} = this.props
    dispatch(closeAllSearchItems())
  }

  handleToggleCirculars(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    this.setState({filterCirculars: !this.state.filterCirculars})
  }

  handleToggleStars(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    this.setState({filterStars: !this.state.filterStars})
  }

  handleUrgentDateChange(dates: $ReadOnlyArray<Date>) {
    this.setState({urgentEndDate: dates[0]})
  }

  handleSuppliersChange(value: Object /*, action: Object*/) {
    this.setState({
      suppliersFilter: value.map((s) => s.value),
    })
  }

  scrollVisible(categoryId: ?number) {
    const doc = document.documentElement
    if (!doc || !this.el) return
    const categoryEl = this.el.querySelector(
      `#category-${categoryId || "null"}`
    )
    if (!categoryEl) return
    const windowTop =
      (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0)
    const {
      top: holderTop,
      // height: holderHeight,
    } = categoryEl.getBoundingClientRect()
    window.scrollTo(
      0,
      windowTop + holderTop - 20 - 80 /* sticky header height */
    )
  }

  getSearchListItems() {
    const {searchListItems} = this.props
    const {
      listType,
      urgentEndDate,
      filterCirculars,
      filterStars,
      suppliersFilter,
    } = this.state
    let list = []
    switch (listType) {
      case "urgent":
        list = getUrgentSearchListItems(searchListItems, urgentEndDate)
        break
      case "critical":
        list = getUrgentSearchListItems(
          getCriticalSearchListItems(searchListItems),
          urgentEndDate
        )
        break
      default:
        list = searchListItems
        break
    }
    if (suppliersFilter.length != 0) {
      list = list.filter((i) => suppliersFilter.includes(i.supplierId))
    }

    if (filterCirculars) {
      list = list.map((i) => ({
        ...i,
        items: i.items.filter((sub) => sub.isCircular),
      }))
    }
    if (filterStars) {
      list = list.map((i) => ({
        ...i,
        items: i.items.filter(
          (sub) =>
            sub.isStar || (sub.parent && sub.parent.isSet && sub.parent.isStar)
        ),
      }))
    }
    return list
  }

  renderCategoryNav() {
    const {categories} = this.props
    if (this.getSearchListItems().length == 0) return null
    return (
      <div className="search-item-category-nav flex-fill">
        <select
          className="form-control form-control-sm"
          name="state"
          onChange={this.handleCategoryChange}
        >
          {categories.map((c) => (
            <option key={`search-item-cat-${c.id}`} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    )
  }

  renderTogglePhotographed() {
    const {t} = this.props
    return (
      <div className="justify-content-end flex ml-2">
        <a
          href="#"
          onClick={this.handleTogglePhotographed}
          className="d-block"
          title={t("dashboard.searchItems.isPhotographed.info")}
        >
          {t("dashboard.searchItems.isPhotographed.toggleAll")}
        </a>
        <span
          title={t("dashboard.searchItems.isPhotographed.info")}
          className="d-inline-block ml-2"
        >
          <Icon
            name="icon-information-white"
            style={{color: "#C6935F", cursor: "help"}}
          />
        </span>
      </div>
    )
  }

  renderCloseAllItems() {
    const {t} = this.props
    if (this.getSearchListItems().length == 0) return null
    return (
      <div className="justify-content-end flex mr-4">
        <a
          href="#"
          onClick={this.handleCloseAllItems}
          className="d-block"
          title={t("dashboard.searchItems.searchItem.closeAll")}
        >
          {t("dashboard.searchItems.searchItem.closeAll")}
        </a>
      </div>
    )
  }

  renderListTypeSubnav() {
    const {t} = this.props
    const {listType} = this.state
    return (
      <div className="subnav-bar container">
        <ul className="subnav">
          <li className="subnav-item">
            <a
              href="#"
              className={ClassNames("subnav-link", {
                "is-active": listType == "all",
              })}
              onClick={(e) => this.handleListTypeClick(e, "all")}
            >
              {t("dashboard.searchItems.listTypeSubnav.all")}
            </a>
          </li>
          <li className="subnav-item">
            <a
              href="#"
              className={ClassNames("subnav-link", {
                "is-active": listType == "urgent",
              })}
              onClick={(e) => this.handleListTypeClick(e, "urgent")}
            >
              {t("dashboard.searchItems.listTypeSubnav.urgent")}
            </a>
          </li>
          <li className="subnav-item">
            <a
              href="#"
              className={ClassNames("subnav-link", {
                "is-active": listType == "critical",
              })}
              onClick={(e) => this.handleListTypeClick(e, "critical")}
            >
              {t("dashboard.searchItems.listTypeSubnav.critical")}
            </a>
          </li>
        </ul>
      </div>
    )
  }

  renderItems() {
    const {
      categories,
      toggledSearchListItems,
      foundRemarksToggleds,
      isStandalone,
    } = this.props
    const searchListItems = this.getSearchListItems()
    const {listType} = this.state
    if (listType == "all") {
      return categories.map((c) => {
        const searchListItemsByCategory = getSearchListItemsByCategory(
          searchListItems,
          c.id
        )
        if (searchListItemsByCategory.length == 0) return
        return (
          <div
            id={`category-${c.id}`}
            className="panel search-item-category"
            key={`search-item-list-cat-${c.id}`}
          >
            {c.name && <h2>{c.name}</h2>}
            <SearchItemList
              keyName={`${c.id}`}
              searchListItems={searchListItemsByCategory}
              toggleds={toggledSearchListItems}
              foundRemarksToggleds={foundRemarksToggleds}
              isStandalone={isStandalone}
            />
          </div>
        )
      })
    } else {
      return (
        <div className="panel">
          <SearchItemList
            searchListItems={searchListItems}
            toggleds={toggledSearchListItems}
            foundRemarksToggleds={foundRemarksToggleds}
            isStandalone={isStandalone}
          />
        </div>
      )
    }
  }

  // Header to show on standalone search item page
  // Outputs the 'opmerkingen klant', only on standalone, because then
  // we are showing the search items for a single baby, no need of repeating
  // the same client info all the time.
  // Also output the list style type (like 'scandinavian')
  renderStandaloneBabyHeader() {
    const {searchListItems, t} = this.props
    const item = getItemForStandalone(searchListItems)
    if (item == null) return null
    return (
      <div className="justify-content-between flex mt-2 mb-4">
        {item.baby.remarks == "" && <div></div>}
        {item.baby.remarks !== "" && (
          <div className="remarks mb-3 p-2">
            <strong>
              {t("dashboard.searchItems.searchItem.remarksPrefix")}
            </strong>
            <br />
            {item.baby.remarks}
          </div>
        )}
        <div className="search-item-meta">
          {item.baby.moodboardType && item.baby.moodboardType !== "" && (
            <span className="moodboard-type">{item.baby.moodboardType}</span>
          )}
          <span className="flags">
            {item.isStar && <Icon name="icon-star" />}
            {item.isPimp && <Icon name="icon-brush" />}
            {item.isExtra && <Icon name="icon-calculator" />}
            {item.isCritical && <Icon name="icon-warning-alt" />}
          </span>

          <time className="small">
            {format(item.baby.pickupDate, "DD/MM/YY")}
          </time>
        </div>
      </div>
    )
  }

  renderUrgentEndDateSelect() {
    return (
      <div style={{width: "400px"}} className="flex align-items-center">
        <div style={{width: "150px"}}>Uiterste pickup:</div>
        <Flatpickr
          options={{
            altFormat: "d/m/Y",
            dateFormat: "Y-m-d",
            altInput: true,
            minDate: new Date(),
          }}
          value={this.state.urgentEndDate}
          onChange={this.handleUrgentDateChange}
          name="urgentEndDate"
        />
      </div>
    )
  }

  renderToggleCirculars() {
    const {t} = this.props
    const className = ClassNames("d-block ml-4", "text-gray-light", {
      "text-link": this.state.filterCirculars,
    })
    return (
      <a
        href="#"
        onClick={this.handleToggleCirculars}
        className={className}
        title={t("dashboard.searchItems.isCircular.info")}
      >
        <Icon name="icon-recycle" />
      </a>
    )
  }

  renderToggleStars() {
    const {t} = this.props
    const className = ClassNames("d-block ml-4", "text-gray-light", {
      "text-link": this.state.filterStars,
    })
    return (
      <a
        href="#"
        onClick={this.handleToggleStars}
        className={className}
        title={t("dashboard.searchItems.isStar.info")}
      >
        <Icon name="icon-star" />
      </a>
    )
  }

  renderSupplierFilter() {
    const {t} = this.props
    const suppliers = this.props.suppliers.map((s) => ({
      value: s.id,
      label: s.name,
    }))
    return (
      <div className="row mb-2">
        <div className="col-lg-7 col-xl-7">
          <div className="flex align-items-center justify-content-between ">
            <div className="mr-2">
              {t("dashboard.searchItems.suppliersFilter.info")}
            </div>
            <div className="w-100">
              <Select
                options={suppliers}
                isMulti
                onChange={this.handleSuppliersChange}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const classNames = ClassNames("dashboard-view search-items-dashboard")
    const {isStandalone} = this.props
    return (
      <div className={classNames} ref={(el) => (this.el = el)}>
        {this.renderListTypeSubnav()}
        <div className="container has-sidebar">
          <div className="row">
            <div className="col-lg-10 col-xl-7">
              <div className="flex align-items-center justify-content-between pt-4 pb-4">
                {this.renderCategoryNav()}
                <div className="flex-fill align-items-center justify-content-end flex">
                  {this.renderCloseAllItems()}
                  {this.state.listType !== "all" &&
                    this.renderUrgentEndDateSelect()}
                  {isStandalone && this.renderTogglePhotographed()}
                  {this.renderToggleCirculars()}
                  {this.renderToggleStars()}
                </div>
              </div>
              {this.renderSupplierFilter()}
              {isStandalone && this.renderStandaloneBabyHeader()}
              {this.renderItems()}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: ReduxState) => ({
  searchListItems: state.searchItems.searchListItems,
  categories: state.searchItems.categories,
  suppliers: state.searchItems.suppliers,
  toggledSearchListItems: state.searchItems.toggleds,
  foundRemarksToggleds: state.searchItems.foundRemarksToggleds,
})

export default withTranslation()(
  connect(mapStateToProps)(SearchItemsDashboardView)
)
